import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import {
  DIGITAL_GC_ONLY_CART,
  MIXED_WITH_BOTH_GC,
  MIXED_WITH_EGC,
  MIXED_WITH_PHYSICAL,
  NON_MIXED_WITH_BOTH_GC,
  PHYSICAL_GC_ONLY_CART,
  RETAIL_ONLY_CART
} from 'constants/cartTypes';
import { CHECKOUT_STEP_MAP, REVIEW_STEP, SHIP_OPTIONS_STEP } from 'constants/melodyCheckoutFlow';

import { SmallLoader } from 'components/Loader';
import SectionChange from 'components/checkout/melody/SectionChange';
import SectionCancel from 'components/checkout/melody/SectionCancel';
import SectionTitle from 'components/checkout/melody/SectionTitle';
import ShippingDowngrade from 'components/checkout/melody/shippingOption/ShippingDowngrade';
import ShippingOption from 'components/checkout/melody/shippingOption/ShippingOption';
import ShippingOptionList from 'components/checkout/melody/shippingOption/ShippingOptionList';
import { getCleanPath } from 'helpers/MelodyCheckoutFlowControl';
import { configurePurchase, onUseShippingDowngradeClick, onUseShippingOptionClick, onViewShippingDowngrade, requestShipOptions, setSelectedShipOptionId } from 'store/ducks/checkout/actions';
import css from 'styles/containers/checkout/melody/shippingOptions.scss';

export class ShippingOptions extends Component {
  componentDidMount = () => {
    const { onViewShippingDowngrade, requestShipOptions, location: { pathname }, shipOption, melodyCheckoutData } = this.props;
    const { isLoaded } = shipOption;
    const isShipOptionStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[SHIP_OPTIONS_STEP];
    const isReviewStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[REVIEW_STEP];

    if (isShipOptionStep && !isLoaded) {
      requestShipOptions();
    }

    if (isReviewStep && this.checkIfCanDowngrade()) {
      const { estimate: { shippingDowngrade } } = melodyCheckoutData;
      const { points: rewardsPoints, dollar_value: dollarAmount } = shippingDowngrade['next-wow'] || { dollar_value: 0, points: 0 };
      onViewShippingDowngrade({ dollarAmount, offeredSpeed: 1, rewardsPoints });
    }
  }

  componentDidUpdate(prevProps) {
    const { location: { pathname: prevPathName } } = prevProps;
    const {
      onViewShippingDowngrade,
      requestShipOptions,
      location: { pathname: nextPathName },
      melodyCheckoutData: { purchase: { shippingAddressId }, estimate: { shippingDowngrade } },
      shipOption: { isLoaded }
    } = this.props;
    const isShipOptionsStep = getCleanPath(nextPathName) === CHECKOUT_STEP_MAP[SHIP_OPTIONS_STEP];
    const isReviewStep = getCleanPath(nextPathName) === CHECKOUT_STEP_MAP[REVIEW_STEP];

    if (!isLoaded && isShipOptionsStep && shippingAddressId && prevPathName !== nextPathName) {
      requestShipOptions();
    }

    if (prevPathName !== nextPathName && isReviewStep && this.checkIfCanDowngrade()) {
      const { points: rewardsPoints, dollar_value: dollarAmount } = shippingDowngrade['next-wow'] || { dollar_value: 0, points: 0 };
      onViewShippingDowngrade({ dollarAmount, offeredSpeed: 1, rewardsPoints });
    }
  }

  checkIfCanDowngrade = () => {
    const {
      isEligibleForShippingDowngrade,
      melodyCheckoutData: {
        estimate: { shippingDowngrade },
        purchase: { shipmentSpeed }
      },
      shipOption: { hasNextWow }
    } = this.props;

    const downgradeEstimatePoints = shippingDowngrade['next-wow']?.points;
    return isEligibleForShippingDowngrade && shipmentSpeed !== 'next-wow' && hasNextWow && !!downgradeEstimatePoints;
  }

  onSelectAndUseShippingOptionClick = ({ currentTarget: { dataset: { shippingOptionId } } }) => {
    const { onUseShippingOptionClick, configurePurchase } = this.props;
    onUseShippingOptionClick();
    configurePurchase({ shipmentOptionId: shippingOptionId });
  }

  onSelectShippingOptionClick = ({ currentTarget: { dataset: { shippingOptionId } } }) => {
    this.props.setSelectedShipOptionId(shippingOptionId);
  }

  onUseShippingOptionClick = e => {
    e.preventDefault();
    const { onUseShippingOptionClick, configurePurchase, melodyCheckoutData: { selectedShipOptionId }, shipOption: { savedShipOptions } } = this.props;
    const shipmentOption = (savedShipOptions || []).find(
      item => item.name === selectedShipOptionId || item.id === selectedShipOptionId) || {};
    onUseShippingOptionClick();
    configurePurchase({ advanceOnSuccess: true, shipmentOptionId: shipmentOption.id });
  }

  onSwitchToNonRushClicked = e => {
    e.preventDefault();

    const { configurePurchase, onUseShippingDowngradeClick, melodyCheckoutData, shipOption } = this.props;
    const { isLoading: shipOptionsLoading, savedShipOptions, isDowngrading } = shipOption;
    const { isLoading: purchaseDataIsLoading, estimate: { shippingDowngrade } } = melodyCheckoutData;

    if (shipOptionsLoading || purchaseDataIsLoading || isDowngrading) {
      return;
    }

    const shipmentOption = (savedShipOptions || []).find(
      item => item.name === 'next-wow') || false;

    if (shipmentOption) {
      const { points: rewardsPoints, dollar_value: dollarAmount } = shippingDowngrade['next-wow'] || { dollar_value: 0, points: 0 };
      onUseShippingDowngradeClick({ dollarAmount, offeredSpeed: 1, rewardsPoints });
      configurePurchase({ advanceOnSuccess: true, shipmentOptionId: shipmentOption.id });
    }
  }

  render() {
    const { isAssignedExposeShipOptions, isEligibleForShippingDowngrade, location: { pathname }, melodyCheckoutData, shipOption } = this.props;
    const { isLoaded: shipOptionsLoaded, isLoading: shipOptionsLoading, savedShipOptions, isDowngrading } = shipOption;
    const {
      canCancelShipOptions,
      canChangeShipOptions,
      cartType,
      estimate: { shippingDowngrade },
      links,
      selectedShipOptionId,
      isLoading: purchaseDataIsLoading,
      purchase
    } = melodyCheckoutData;
    const { deliveryPromise, promise, chargeSummary: { shippingCharge }, shipmentSpeed } = purchase;
    const isReviewStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[REVIEW_STEP];
    const showShippingDowngradeBonus = isEligibleForShippingDowngrade && shipmentSpeed === 'next-wow';
    const componentParams = {
      deliveryPromise,
      hasDigitalDelivery: false,
      hasRetailDelivery: false,
      isReviewStep,
      promise,
      showShippingDowngradeBonus,
      shippingCharge
    };

    switch (cartType) {
      case NON_MIXED_WITH_BOTH_GC:
      case MIXED_WITH_BOTH_GC:
      case MIXED_WITH_EGC:
        componentParams.hasDigitalDelivery = true;
        componentParams.hasRetailDelivery = true;
        break;
      case DIGITAL_GC_ONLY_CART:
        componentParams.hasDigitalDelivery = true;
        break;
      case PHYSICAL_GC_ONLY_CART:
      case RETAIL_ONLY_CART:
      case MIXED_WITH_PHYSICAL:
        componentParams.hasRetailDelivery = true;
        break;
    }

    const { points: downgradeEstimatePoints, dollar_value: downgradeEstimateDollars } = shippingDowngrade['next-wow'] || { dollar_value: null, points: null };
    componentParams.downgradeEstimatePoints = downgradeEstimatePoints;
    componentParams.downgradeEstimateDollars = downgradeEstimateDollars;
    const isShipOptionStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[SHIP_OPTIONS_STEP];

    const sectionTitle = (<SectionTitle
      id="shipping-method-section"
      isComplete={true}
      step="3"
      title="Shipping Method"/>);

    if (isAssignedExposeShipOptions) {
      return (
        <ul className={cn(css.section, { [css.eso]: !(isReviewStep && this.checkIfCanDowngrade()) })}>
          <li>{sectionTitle}</li>
          <li />
          <li>
            {
              shipOptionsLoading
                ? <SmallLoader />
                : <>
                <div className={cn({ [css.estimatedDelivery]: cartType !== DIGITAL_GC_ONLY_CART })}><ShippingOption {...componentParams} /></div>
                { canChangeShipOptions && <ShippingOptionList
                  isAssignedExposeShipOptions={isAssignedExposeShipOptions}
                  isEligibleForShippingDowngrade={isEligibleForShippingDowngrade}
                  purchaseDataIsLoading={purchaseDataIsLoading}
                  shippingDowngrade={shippingDowngrade}
                  shippingOptions={savedShipOptions}
                  shipOptionsLoaded={shipOptionsLoaded}
                  shipOptionsLoading={shipOptionsLoading}
                  onSelectAndUseShippingOptionClick={this.onSelectAndUseShippingOptionClick}
                  onSelectShippingOptionClick={this.onSelectShippingOptionClick}
                  onUseShippingOptionClick={this.onUseShippingOptionClick}
                  selectedShippingOptionId={selectedShipOptionId} />
                }
                </>
            }
          </li>
          {
            isReviewStep && this.checkIfCanDowngrade() && <li>
              <ShippingDowngrade
                downgradeEstimateDollars={downgradeEstimateDollars}
                isLoading={isDowngrading}
                onSwitchToNonRushClicked={this.onSwitchToNonRushClicked} />
            </li>
          }
        </ul>
      );
    }

    if (isShipOptionStep) {
      return (
        <ul className={css.sectionOpen}>
          <li>{sectionTitle}</li>
          <li><SectionCancel
            labelledby="shipping-method-section"
            to={links[REVIEW_STEP]}
            showLink={!purchaseDataIsLoading && canCancelShipOptions} /></li>
          <li>
            {
              shipOptionsLoading
                ? <SmallLoader />
                : <ShippingOptionList
                  isEligibleForShippingDowngrade={isEligibleForShippingDowngrade}
                  purchaseDataIsLoading={purchaseDataIsLoading}
                  shippingDowngrade={shippingDowngrade}
                  shippingOptions={savedShipOptions}
                  shipOptionsLoaded={shipOptionsLoaded}
                  shipOptionsLoading={shipOptionsLoading}
                  onSelectShippingOptionClick={this.onSelectShippingOptionClick}
                  onUseShippingOptionClick={this.onUseShippingOptionClick}
                  selectedShippingOptionId={selectedShipOptionId} />
            }
          </li>
        </ul>
      );
    }

    return (
      <ul className={css.section}>
        <li>{sectionTitle}</li>
        <li><SectionChange
          labelledby="shipping-method-section"
          to={links[SHIP_OPTIONS_STEP]}
          showLink={!purchaseDataIsLoading && canChangeShipOptions} /></li>
        <li><ShippingOption {...componentParams} /></li>
        {
          isReviewStep && this.checkIfCanDowngrade() && <li>
            <ShippingDowngrade
              downgradeEstimateDollars={downgradeEstimateDollars}
              isLoading={isDowngrading}
              onSwitchToNonRushClicked={this.onSwitchToNonRushClicked} />
          </li>
        }
      </ul>
    );
  }
}

function mapStateToProps({ melodyCheckoutData, shipOption, routing }) {
  return {
    shipOption,
    melodyCheckoutData,
    location: routing.locationBeforeTransitions
  };
}

ShippingOptions.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  onUseShippingDowngradeClick,
  onUseShippingOptionClick,
  onViewShippingDowngrade,
  configurePurchase,
  requestShipOptions,
  setSelectedShipOptionId
})(ShippingOptions);
